// CSS custom-property emitter.
//
// Tokens are plain TypeScript values; this turns them into the `--herefor-*` custom
// properties stylesheets consume. Framework-agnostic by design (bead
// hf-e2-design-shell-ej8.1): no React, no CSS-in-JS runtime, no build plugin. A
// consumer either imports the values directly or drops this string into a <style>.
//
// Why custom properties rather than a compiled stylesheet: they survive into the
// browser, so forced-colours and user stylesheets can override them, and the print
// surface can re-point them without a rebuild.

import {
  color,
  elevation,
  focusRing,
  fontSize,
  fontWeight,
  lineHeight,
  motion,
  pastDisplay,
  radius,
  space,
  stateColor,
} from "./tokens";

const PREFIX = "--herefor";

function block(entries: readonly (readonly [string, string])[]): string {
  return entries.map(([name, value]) => `  ${name}: ${value};`).join("\n");
}

/** Every token as a `[customProperty, value]` pair. Exported so tests can assert coverage. */
export function tokenCustomProperties(): (readonly [string, string])[] {
  const out: (readonly [string, string])[] = [];

  for (const [key, value] of Object.entries(color))
    out.push([`${PREFIX}-color-${kebab(key)}`, value]);
  for (const [key, value] of Object.entries(fontSize))
    out.push([`${PREFIX}-font-size-${kebab(key)}`, value]);
  for (const [key, value] of Object.entries(fontWeight))
    out.push([`${PREFIX}-font-weight-${kebab(key)}`, String(value)]);
  for (const [key, value] of Object.entries(lineHeight))
    out.push([`${PREFIX}-line-height-${kebab(key)}`, String(value)]);
  for (const [key, value] of Object.entries(space))
    out.push([`${PREFIX}-space-${kebab(key)}`, value]);
  for (const [key, value] of Object.entries(radius))
    out.push([`${PREFIX}-radius-${kebab(key)}`, value]);
  for (const [key, value] of Object.entries(elevation))
    out.push([`${PREFIX}-elevation-${kebab(key)}`, value]);
  for (const [key, value] of Object.entries(motion))
    out.push([`${PREFIX}-motion-${kebab(key)}`, value]);

  out.push([`${PREFIX}-focus-width`, focusRing.width]);
  out.push([`${PREFIX}-focus-offset`, focusRing.offset]);
  out.push([`${PREFIX}-focus-color`, focusRing.color]);
  out.push([`${PREFIX}-focus-offset-color`, focusRing.offsetColor]);
  out.push([`${PREFIX}-focus-ring`, focusRing.style]);

  for (const [stateKey, token] of Object.entries(stateColor)) {
    out.push([`${PREFIX}-state-${kebab(stateKey)}-fg`, token.fg]);
    out.push([`${PREFIX}-state-${kebab(stateKey)}-bg`, token.bg]);
    out.push([`${PREFIX}-state-${kebab(stateKey)}-border`, token.border]);
  }

  out.push([`${PREFIX}-past-fg`, pastDisplay.fg]);
  out.push([`${PREFIX}-past-bg`, pastDisplay.bg]);
  out.push([`${PREFIX}-past-border`, pastDisplay.border]);

  return out;
}

function kebab(key: string): string {
  return key.replace(/([a-z0-9])([A-Z])/g, "$1-$2").toLowerCase();
}

/**
 * The complete stylesheet.
 *
 * The reduced-motion block is emitted here rather than left to component authors on
 * purpose: `prefers-reduced-motion` is the kind of thing that gets remembered on the
 * first three components and forgotten on the fourth. Because every duration token is
 * re-pointed to 0ms at the custom-property level, any component built on the tokens
 * respects the preference without doing anything.
 */
export function tokenStylesheet(): string {
  return `:root {
${block(tokenCustomProperties())}
}

@media (prefers-reduced-motion: reduce) {
  :root {
    ${PREFIX}-motion-duration-fast: ${motion.reducedDuration};
    ${PREFIX}-motion-duration-base: ${motion.reducedDuration};
    ${PREFIX}-motion-duration-slow: ${motion.reducedDuration};
  }
}
`;
}
